import { spliceOut } from 'components/sharpr/util/spliceOut';
import { ensure } from 'components/sharpr/ensure/ensure';
import moment from 'moment';
import _ from 'lodash';

export class FilterMessageService {
	asObject(FineTuner) {
		const filters = [];

		// Term
		if (FineTuner.search.term) {
			filters.push({
				text: 'matching',
				groupStart: true,
			});
			ensure
				.string(FineTuner.search.term)
				.split('&&')
				.filter(Boolean)
				.forEach(term => {
					filters.push({
						item: term,
						onRemove: search => {
							const words = ensure.string(search.term).split('&&');
							spliceOut(words, term);
							search.term = words.join('&&');
						},
					});
					filters.push({
						text: 'and',
					});
				});
			filters.pop();
		}

		// Date range
		if (FineTuner.search.datePreset && FineTuner.search.datePreset !== 'all') {
			let prefixText;
			let dateText;
			if (FineTuner.search.datePreset == 'custom' && FineTuner.search.dateStart && FineTuner.search.dateEnd) {
				prefixText = 'between';
				dateText = `${moment(FineTuner.search.dateStart).format('M/D/Y')} and ${moment(FineTuner.search.dateEnd).format(
					'M/D/Y'
				)}`;
			} else if (FineTuner.search.datePreset.indexOf('Last') > -1) {
				prefixText = 'from the';
				dateText = FineTuner.search.datePreset.toLowerCase();
			} else if (FineTuner.search.datePreset.indexOf('This') > -1) {
				prefixText = 'from';
				dateText = FineTuner.search.datePreset.toLowerCase();
			}
			if (prefixText && dateText) {
				// in the case of opening the calendar but not choosing a date yet,
				// we would have no prefixText nor dateText
				filters.push({
					text: prefixText,
					groupStart: true,
				});
				filters.push({
					item: dateText,
					onRemove: _.get(FineTuner.immutableFields, 'date')
						? undefined
						: search => {
								search.datePreset = 'all';
								search.dateStart = undefined;
								search.dateEnd = undefined;
							},
				});
			}
		}

		// Categories
		if (FineTuner.categories && FineTuner.search.categories && FineTuner.search.categories.length > 0) {
			filters.push({
				text: 'categorized as',
				groupStart: true,
			});
			FineTuner.categories.forEach(category => {
				let atIdx = FineTuner.search.categories.indexOf(category);
				if (atIdx === -1) {
					return;
				}
				filters.push({
					item: category,
					onRemove: search => search.categories.splice(atIdx, 1),
				});
				filters.push({
					text: 'or',
				});
			});
			// remove trailing "or"
			filters.pop();
		}

		// :::::: taxonomies ::::::
		if (FineTuner.taxonomies && FineTuner.search.taxonomies) {
			FineTuner.taxonomies.forEach(taxonomy => {
				const taxonomyDetails = FineTuner.search.taxonomies[taxonomy.id] || {};
				const selectedOptions = Array.isArray(taxonomyDetails.options) ? taxonomyDetails.options : [];
				const matches = taxonomyDetails.matches || 'any';
				const matchAll = matches === 'all';

				if (selectedOptions && selectedOptions.length > 0) {
					filters.push({
						text: `with ${taxonomy.name.toLowerCase()}`,
						groupStart: true,
					});
				}

				const options = taxonomy.options || [];

				if (
					taxonomy.type.toLowerCase() === 'autosuggest' &&
					(taxonomy.maxAllowedResponses === null || taxonomy.maxAllowedResponses > 1) &&
					selectedOptions.length > 1
				) {
					filters.push({
						text: matches,
						cssClass: 'has-options',
						onOption: search => {
							if (!search.taxonomies[taxonomy.id]) {
								search.taxonomies[taxonomy.id] = { options: [] };
							}

							search.taxonomies[taxonomy.id].matches = matchAll ? 'any' : 'all';
						},
					});

					filters.push({ text: 'options' });
				}

				// add selected options
				selectedOptions.forEach(optionId => {
					// find the option name
					const option = options.find(o => o.id === optionId);
					const optionName = option ? option.name : `Option ${optionId}`;

					filters.push({
						item: optionName,
						onRemove: search => {
							if (!search.taxonomies[taxonomy.id] || !Array.isArray(search.taxonomies[taxonomy.id].options)) {
								return;
							}

							const idx = search.taxonomies[taxonomy.id].options.indexOf(optionId);
							if (idx > -1) {
								search.taxonomies[taxonomy.id].options.splice(idx, 1);

								if (search.taxonomies[taxonomy.id].options.length === 0) {
									delete search.taxonomies[taxonomy.id];
								}
							}
						},
					});

					// :: add 'and' or 'or' between options
					if (selectedOptions.length > 1) {
						filters.push({
							text: matchAll ? 'and' : 'or',
						});
					}
				});

				// :: remove trailing 'and' or 'or'
				if (selectedOptions.length > 1) {
					filters.pop();
				}
			});
		}

		// Tags
		let tags = [];
		if (FineTuner.tags && FineTuner.search.tags && FineTuner.search.tags.length > 0) {
			tags = FineTuner.search.tags.filter(t => t !== '(Untagged)');
		}
		if (tags.length > 0) {
			if (tags.length === 1) {
				// a single tag
				filters.push({
					text: 'with tag',
					groupStart: true,
				});
				let tag = tags[0];
				filters.push({
					item: tag,
					onRemove: search => {
						let atIdx = search.tags.indexOf(tag);
						search.tags.splice(atIdx, 1);
					},
				});
			} else {
				// multiple tags
				filters.push({
					text: 'with',
					groupStart: true,
				});
				filters.push({
					text: FineTuner.search.tagsAll ? 'all' : 'any',
					cssClass: 'has-options',
					// when you click "any" or "all" it toggles between
					onOption: search => {
						search.tagsAll = !search.tagsAll;
					},
				});
				filters.push({
					text: FineTuner.search.tagsAll ? 'tags' : 'tag',
				});
				tags.forEach(tag => {
					filters.push({
						item: tag,
						onRemove: search => {
							let atIdx = search.tags.indexOf(tag);
							search.tags.splice(atIdx, 1);
						},
					});
					filters.push({
						type: 'tag-filter-boolean',
						text: FineTuner.search.tagsAll ? 'and' : 'or',
					});
				});
				// remove trailing "or"
				filters.pop();
			}
		}

		// Classifications
		if (FineTuner.classifications && FineTuner.search.classifications && FineTuner.search.classifications.length > 0) {
			filters.push({
				text: 'classified under',
				groupStart: true,
			});
			FineTuner.search.classifications.forEach(classificationName => {
				let classification = undefined;
				FineTuner.classifications.some(cls => {
					if (classificationName === cls.name) {
						classification = cls.name;
						return true;
					}
				});
				if (!classification) {
					// bummer dude
					console.log('classification not found', classificationName);
					return;
				}
				filters.push({
					item: classification,
					onRemove: search => {
						let atIdx = search.classifications.indexOf(classificationName);
						search.classifications.splice(atIdx, 1);
					},
				});
				filters.push({
					text: 'or',
				});
			});
			// remove trailing "or"
			filters.pop();
		}

		// Publisher
		if (FineTuner.users && FineTuner.search.publishers && FineTuner.search.publishers.length > 0) {
			filters.push({
				text: 'published by',
				groupStart: true,
			});
			FineTuner.users.forEach(user => {
				let atIdx = FineTuner.search.publishers.indexOf(user.id);
				if (atIdx == -1) {
					return;
				}
				filters.push({
					item: user.name,
					onRemove: search => search.publishers.splice(atIdx, 1),
				});
				filters.push({
					text: 'or',
				});
			});
			// remove trailing "or"
			filters.pop();
		}

		// From websites
		if (FineTuner.domains && FineTuner.search.domains && FineTuner.search.domains.length > 0) {
			filters.push({
				text: 'from',
				groupStart: true,
			});
			FineTuner.search.domains.forEach(domain => {
				filters.push({
					item: domain,
					onRemove: search => {
						let atIdx = search.domains.indexOf(domain);
						search.domains.splice(atIdx, 1);
					},
				});
				filters.push({
					text: 'or',
				});
			});
			// remove trailing "or"
			filters.pop();
		}

		// Was emailed
		if (FineTuner.search.wasEmailed === true) {
			filters.push({
				text: 'that have',
				groupStart: true,
			});
			filters.push({
				item: 'been emailed',
				onRemove: search => (search.wasEmailed = undefined),
			});
		}

		// Not emailed
		if (FineTuner.search.wasEmailed === false) {
			filters.push({
				text: 'that have',
				groupStart: true,
			});
			filters.push({
				text: 'never been emailed',
				onRemove: search => (search.wasEmailed = undefined),
			});
		}

		// Part of an Insight Brief
		if (FineTuner.search.inStory === true) {
			filters.push({
				text: 'that are',
				groupStart: true,
			});
			filters.push({
				text: 'part of an insight brief',
				onRemove: search => (search.inStory = undefined),
			});
		}

		// Not part of an Insight Brief
		if (FineTuner.search.inStory === false) {
			filters.push({
				text: 'that are',
				groupStart: true,
			});
			filters.push({
				text: 'not part of an insight brief',
				onRemove: search => (search.inStory = undefined),
			});
		}

		// Untagged
		if (FineTuner.search.tags && FineTuner.search.tags.indexOf('(Untagged)') > -1) {
			FineTuner.search.untagged = true;
			filters.push({
				text: 'that are',
				groupStart: true,
			});
			filters.push({
				text: 'untagged',
				onRemove: search => {
					search.untagged = undefined;
					let atIdx = search.tags.indexOf('(Untagged)');
					if (atIdx > -1) {
						search.tags.splice(atIdx, 1);
					}
				},
			});
		} else {
			FineTuner.search.untagged = undefined;
		}

		// Archive Filter
		if (FineTuner.search.archiveAction && FineTuner.search.archiveDatePreset) {
			const archiveFilterText = this._getArchiveFilterText(
				FineTuner.search.archiveAction,
				FineTuner.search.archiveDatePreset
			);
			filters.push({
				text: archiveFilterText.groupStartText,
				groupStart: true,
			});
			filters.push({
				text: archiveFilterText.filterText,
				onRemove: search => {
					search.archiveAction = undefined;
					search.archiveDatePreset = undefined;
				},
			});
		}

		// Client Intel
		if (FineTuner.search.clientIntel == 1) {
			filters.push({
				text: 'that are',
				groupStart: true,
			});
			filters.push({
				text: 'approved for clients',
				onRemove: search => (search.clientIntel = undefined),
			});
		}

		// Approval status
		if (FineTuner.search.approvalStatus == 'approved') {
			filters.push({
				text: 'that are',
				groupStart: true,
			});
			filters.push({
				text: 'approved',
				onRemove: search => (search.approvalStatus = undefined),
			});
		} else if (FineTuner.search.approvalStatus == 'unapproved') {
			filters.push({
				text: 'that',
				groupStart: true,
			});
			filters.push({
				text: 'need approval',
				onRemove: search => (search.approvalStatus = undefined),
			});
		}
		// My Favorites
		if (FineTuner.search.myFavorites) {
			filters.push({
				text: 'that I',
				groupStart: true,
			});
			filters.push({
				text: 'favorited',
				onRemove: search => (search.myFavorites = undefined),
			});
		}
		return filters;
	}

	asText(FineTuner) {
		return this.asObject(FineTuner)
			.map(filter => (filter.text ? filter.text : `"${filter.item}"`))
			.join(' ');
	}

	_getArchiveFilterText(archiveAction, archiveDatePreset) {
		let groupStartText = '';
		let filterText = '';
		switch (archiveAction) {
			case 'active_and_archived':
				groupStartText = 'that are';
				filterText = `active or have been archived in the last ${archiveDatePreset}`;
				break;
			case 'archive past':
				groupStartText = 'that are';
				filterText = `archived in the last ${archiveDatePreset}`;
				break;
			case 'archive future':
				groupStartText = 'that will be';
				filterText = `archived in the next ${archiveDatePreset}`;
				break;
			case 'destroy':
				groupStartText = 'that will be';
				filterText = `destroyed in the next ${archiveDatePreset}`;
				break;
			case 'both':
				groupStartText = 'that will be';
				filterText = `archived or destroyed in the next ${archiveDatePreset}`;
				break;
			default:
				break;
		}
		return { groupStartText, filterText };
	}
}
